import React from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft,
  Settings,
  Eye,
  Gift,
  Code,
  Palette,
  BarChart3
} from 'lucide-react'

const MomentPerksPage: React.FC = () => {
  const navigate = useNavigate()
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 24, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  }

  const features = [
    {
      icon: <Code className="w-6 h-6" />,
      title: "Developer Friendly",
      description: "Simple SDK integration with extensive documentation"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Fully Customizable", 
      description: "Match your brand with complete design control"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Performance Driven",
      description: "Optimized for speed and conversion rates"
    }
  ]

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F9FA]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Fixed Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-[#6B7280] hover:text-[#1A2B49] transition-colors duration-200 font-medium"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
            
            <div className="hidden sm:flex items-center space-x-1">
              <div className="w-2 h-2 bg-[#FE4215] rounded-full"></div>
              <div className="w-2 h-2 bg-[#E5E7EB] rounded-full"></div>
              <div className="w-2 h-2 bg-[#E5E7EB] rounded-full"></div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content Container */}
      <div className="flex-1 pt-16">
        {/* Hero Section - Full Screen */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#FE4215]/10 to-[#1A2B49]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-[#1A2B49]/10 to-[#FE4215]/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center w-full"
          >
            {/* Logo/Icon */}
            <motion.div variants={itemVariants} className="mb-16">
              <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-[#FE4215] to-[#E53910] rounded-2xl shadow-lg">
                <Gift className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              </div>
            </motion.div>
            
            {/* Main Heading */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A2B49] mb-12 tracking-tight"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Moment
              <span className="bg-gradient-to-r from-[#FE4215] to-[#E53910] bg-clip-text text-transparent">
                Perks
              </span>
            </motion.h1>
            
            {/* Subtitle */}
            <motion.p
              variants={itemVariants}
              className="text-lg sm:text-xl lg:text-2xl text-[#6B7280] max-w-5xl mx-auto leading-relaxed mb-16"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Transform customer interactions into revenue opportunities with{' '}
              <span className="font-semibold text-[#1A2B49]">strategic engagement moments</span>
              {' '}powered by MomentScience.
            </motion.p>

            {/* CTA Section */}
            <motion.div variants={itemVariants} className="mb-20">
              <h2 className="text-3xl sm:text-4xl font-semibold text-[#1A2B49] mb-12" style={{ fontFamily: 'Inter, sans-serif' }}>
                How do you want to see Moments Integration?
              </h2>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 max-w-3xl mx-auto">
                <button 
                  onClick={() => navigate('/moment-customization')}
                  className="group w-full sm:w-auto inline-flex items-center justify-center space-x-3 px-10 py-5 bg-gradient-to-r from-[#FE4215] to-[#E53910] text-white rounded-lg hover:from-[#E53910] hover:to-[#D73510] transition-all duration-300 font-semibold text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '8px'
                  }}
                >
                  <Settings className="w-7 h-7 group-hover:rotate-90 transition-transform duration-300" />
                  <span>Customize Moments</span>
                </button>
                
                <button 
                  onClick={() => navigate('/moments-gallery')}
                  className="group w-full sm:w-auto inline-flex items-center justify-center space-x-3 px-10 py-5 bg-[#1A2B49] text-white rounded-lg hover:bg-[#233D71] transition-all duration-300 font-semibold text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  style={{ 
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '8px'
                  }}
                >
                  <Eye className="w-7 h-7 group-hover:scale-110 transition-transform duration-300" />
                  <span>View Examples</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

        {/* Features Overview */}
        <section className="py-6 sm:py-8 lg:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-8"
          >
            <motion.h2
              variants={itemVariants}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A2B49] mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              What are 
              <span className="bg-gradient-to-r from-[#FE4215] to-[#E53910] bg-clip-text text-transparent"> Moments</span>?
            </motion.h2>
            
            <motion.p
              variants={itemVariants}
              className="text-xl text-[#6B7280] max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Strategic touchpoints in your customer's journey where you can present 
              <span className="font-semibold text-[#1A2B49]"> personalized offers, exclusive deals, and engaging content</span> 
              to transform interactions into revenue opportunities.
            </motion.p>
          </motion.div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-white rounded-lg p-8 shadow-sm border border-[#E5E7EB] hover:shadow-lg hover:border-[#D1D5DB] transition-all duration-300"
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                }}
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-[#F3F4F6] to-[#E5E7EB] rounded-lg flex items-center justify-center text-[#6B7280] group-hover:from-[#FEF3F2] group-hover:to-[#FDE8E7] group-hover:text-[#FE4215] transition-all duration-300" style={{ borderRadius: '8px' }}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-[#1A2B49]" style={{ fontFamily: 'Inter, sans-serif' }}>{feature.title}</h3>
                </div>
                <p className="text-[#6B7280] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>{feature.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Integration Options */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* JS SDK */}
            <motion.div
              variants={itemVariants}
              className="group relative bg-gradient-to-br from-[#FEF3F2] to-[#FDE8E7] rounded-lg p-8 lg:p-10 border border-[#FECACA] hover:shadow-lg transition-all duration-300"
              style={{ borderRadius: '12px' }}
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-r from-[#FE4215] to-[#E53910] rounded-lg flex items-center justify-center" style={{ borderRadius: '8px' }}>
                  <Settings className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1A2B49]" style={{ fontFamily: 'Inter, sans-serif' }}>JS SDK Integration</h3>
                  <p className="text-[#FE4215] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Web Applications</p>
                </div>
              </div>
              <p className="text-[#6B7280] leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                Quick and easy integration with comprehensive customization options. Perfect for websites and web applications with full design control.
              </p>
              <div className="flex items-center text-[#FE4215] font-medium group-hover:text-[#E53910] transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                <span>Learn more about SDK</span>
                <ArrowLeft className="w-4 h-4 ml-2 rotate-180 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>

            {/* API */}
            <motion.div
              variants={itemVariants}
              className="group relative bg-gradient-to-br from-[#F8FAFC] to-[#F1F5F9] rounded-lg p-8 lg:p-10 border border-[#E2E8F0] hover:shadow-lg transition-all duration-300"
              style={{ borderRadius: '12px' }}
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-r from-[#1A2B49] to-[#233D71] rounded-lg flex items-center justify-center" style={{ borderRadius: '8px' }}>
                  <Eye className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#1A2B49]" style={{ fontFamily: 'Inter, sans-serif' }}>API Integration</h3>
                  <p className="text-[#6B7280] font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>Maximum Flexibility</p>
                </div>
              </div>
              <p className="text-[#6B7280] leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                Maximum flexibility with custom rendering and backend integration capabilities. Build exactly what you envision.
              </p>
              <div className="flex items-center text-[#6B7280] font-medium group-hover:text-[#1A2B49] transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                <span>Explore API documentation</span>
                <ArrowLeft className="w-4 h-4 ml-2 rotate-180 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

        {/* CTA Section placeholder - removed Benefits Section as requested */}

        {/* CTA Section */}
        <section className="py-6 sm:py-8 lg:py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.h2
              variants={itemVariants}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A2B49] mb-4"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Ready to get started?
            </motion.h2>
            
            <motion.p
              variants={itemVariants}
              className="text-xl text-[#6B7280] mb-6"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Choose your integration path and start building amazing customer experiences today.
            </motion.p>
            
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
            >
              <button 
                onClick={() => navigate('/moment-customization')}
                className="group w-full sm:w-auto inline-flex items-center justify-center space-x-3 px-8 py-4 bg-gradient-to-r from-[#FE4215] to-[#E53910] text-white rounded-lg hover:from-[#E53910] hover:to-[#D73510] transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                style={{ 
                  fontFamily: 'Inter, sans-serif',
                  borderRadius: '8px'
                }}
              >
                <Settings className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" />
                <span>Start Customizing</span>
              </button>
              
              <button 
                onClick={() => navigate('/moments-gallery')}
                className="group w-full sm:w-auto inline-flex items-center justify-center space-x-3 px-8 py-4 bg-white text-[#1A2B49] border-2 border-[#E5E7EB] rounded-lg hover:border-[#D1D5DB] hover:bg-[#F9FAFB] transition-all duration-300 font-semibold text-lg shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                style={{ 
                  fontFamily: 'Inter, sans-serif',
                  borderRadius: '8px'
                }}
              >
                <Eye className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                <span>View Examples</span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>
      </div>
    </div>
  )
}

export default MomentPerksPage
